import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {WorkstreamExecSponsorInboxWorkstream} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {t} from 'i18next';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useWorkstream} from 'views/workstreams/hooks';
import {
  getInboxPendingCount,
  useExecSponsorInboxDetail,
  useExecSponsorInboxWorkstreams,
  useMarkReviewItemReviewed,
} from 'views/workstreams/inbox/useExecSponsorInbox';
import {
  getPendingReviewItemKey,
  isReviewedReviewItem,
  isSupportedReviewItem,
  NeedsResolutionSection,
  ReviewItemsSection,
} from 'views/workstreams/inbox/WorkstreamDetail';
import {isRestrictedWorkstreamAccessResponse} from 'views/workstreams/utils';

import {FadeIn} from './FadeIn';
import {SectionFoldToggle} from './SectionFoldToggle';
import {InsetDivider, ScanRow} from './WorkstreamScanList';

/**
 * /workstreams (`?exec=1`): the exec sponsor inbox as a rail place.
 * One feed instead of /workstreams/exec-sponsor-inbox's list + detail: per
 * sponsored workstream (ones with something to review first), its list card
 * (status, title, latest update; opens like any card), and in the same box its
 * needs-resolution items, unread items, then a Reviewed fold. No title; the
 * rail's Exec inbox row says where you are.
 */
export const InboxPane = ({onLeaveUpdate}: {onLeaveUpdate: (id: number) => void}) => {
  const hasLoggedViewRef = useRef(false);
  const {data, isPending, isError, refetch} = useExecSponsorInboxWorkstreams();

  // Ones with something to review first, in the order of the first load, so a
  // card doesn't jump down as you clear it.
  const firstOrderRef = useRef<Map<number, number> | null>(null);
  const workstreams = useMemo(() => {
    const all = data?.workstreams ?? [];
    if (all.length > 0 && !firstOrderRef.current) {
      const ordered = [
        ...all.filter((workstream) => getInboxPendingCount(workstream) > 0),
        ...all.filter((workstream) => getInboxPendingCount(workstream) === 0),
      ];
      firstOrderRef.current = new Map(ordered.map((workstream, index) => [workstream.id, index]));
    }
    const rank = (id: number) => firstOrderRef.current?.get(id) ?? Number.MAX_SAFE_INTEGER;
    return [...all].sort((a, b) => rank(a.id) - rank(b.id));
  }, [data?.workstreams]);

  useEffect(() => {
    if (!data || hasLoggedViewRef.current) return;
    hasLoggedViewRef.current = true;
    analyticsLogger().logEvent('EXEC_SPONSOR_INBOX_VIEWED', {
      workstream_count: data.workstreams?.length ?? 0,
      surface: 'workstreams4',
    });
  }, [data]);

  if (isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        paddingY="44"
        style={{gap: 16}}
      >
        <Text color="subtle">{t('workstream_exec_sponsor_inbox_load_error')}</Text>
        <Button variant="outline" size="small" onClick={() => void refetch()}>
          {t('retry')}
        </Button>
      </Box>
    );
  }

  if (isPending) {
    return (
      <Box display="flex" justifyContent="center" paddingY="44">
        <Spinner size="small" />
      </Box>
    );
  }

  return (
    // The list's frame (WorkstreamScanList): page gutter, reading cap.
    <Box paddingTop="24" paddingBottom="16">
      <Layout.MaxWidth width="reading" inset>
        {workstreams.length === 0 ? (
          <Text size="small" color="subtle">
            {t('workstream_exec_sponsor_inbox_empty_title')}
          </Text>
        ) : (
          <Stack gap="16">
            {workstreams.map((workstream) => (
              <InboxCard
                key={workstream.id}
                workstream={workstream}
                onLeaveUpdate={onLeaveUpdate}
              />
            ))}
          </Stack>
        )}
      </Layout.MaxWidth>
    </Box>
  );
};

// One box: the workstream's list card, a divider, then what's in the inbox for it.
const InboxCard = ({
  workstream,
  onLeaveUpdate,
}: {
  workstream: WorkstreamExecSponsorInboxWorkstream;
  onLeaveUpdate: (id: number) => void;
}) => {
  const {workstream: full, isLoading} = useWorkstream(workstream.id);
  const scanWorkstream = full && !isRestrictedWorkstreamAccessResponse(full) ? full : undefined;

  return (
    <Box
      borderStyle="Solid"
      borderWidth="1"
      borderColor="Border Subtle"
      borderRadius="Medium"
      style={{overflow: 'hidden'}}
    >
      {scanWorkstream ? (
        <ScanRow workstream={scanWorkstream} depth={0} onLeaveUpdate={onLeaveUpdate} />
      ) : (
        <Box padding="16">
          {isLoading ? <Skeleton.Text width={240} /> : <Text isBold>{workstream.name}</Text>}
        </Box>
      )}
      <InsetDivider />
      <InboxItems workstream={workstream} />
    </Box>
  );
};

const InboxItems = ({workstream}: {workstream: WorkstreamExecSponsorInboxWorkstream}) => {
  const [reviewedOpen, setReviewedOpen] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const pendingCount = getInboxPendingCount(workstream);
  // Quiet workstreams only load their items once you open Reviewed.
  const {data, isPending} = useExecSponsorInboxDetail(workstream.id, {
    enabled: pendingCount > 0 || reviewedOpen,
  });
  const reviewMutation = useMarkReviewItemReviewed(workstream.id);

  const items = (data?.review_items ?? []).filter(isSupportedReviewItem);
  const unreadItems = items.filter((item) => !isReviewedReviewItem(item));
  const reviewedCount = items.length - unreadItems.length;

  const markAllReviewed = async () => {
    setIsMarkingAll(true);
    try {
      for (const item of unreadItems) {
        await reviewMutation.mutateAsync(item);
      }
    } finally {
      setIsMarkingAll(false);
    }
  };

  return (
    // The sections bring their own 24px above; quiet cards get the card's 16.
    <Box paddingX="16" paddingBottom="16" paddingTop={pendingCount === 0 ? '16' : undefined}>
      {pendingCount === 0 ? (
        <Text size="small" color="subtle">
          Nothing new
        </Text>
      ) : isPending ? (
        <Box paddingTop="16">
          <Skeleton.Box height={96} />
        </Box>
      ) : (
        <>
          {unreadItems.length > 1 && (
            <Box display="flex" justifyContent="flex-end" paddingTop="16">
              <Button
                variant="outline"
                size="small"
                isLoading={isMarkingAll}
                onClick={() => void markAllReviewed()}
              >
                Mark all reviewed
              </Button>
            </Box>
          )}
          <NeedsResolutionSection
            workstreamId={workstream.id}
            needsResolution={data?.needs_resolution}
          />
          {unreadItems.length > 0 && (
            <ReviewItemsSection
              items={data?.review_items ?? []}
              isReviewed={false}
              pendingReviewItemKey={getPendingReviewItemKey(reviewMutation)}
              onReview={(item) => reviewMutation.mutate(item)}
            />
          )}
        </>
      )}

      {/* Until a quiet workstream's items load, its fold has no count. */}
      {(!data || reviewedCount > 0 || reviewedOpen) && (
        <Box marginTop="16">
          <SectionFoldToggle
            fold="inbox_reviewed"
            label={t('workstream_exec_sponsor_inbox_reviewed')}
            detail={reviewedCount > 0 ? reviewedCount : undefined}
            open={reviewedOpen}
            onToggle={() => setReviewedOpen((open) => !open)}
          />
          {reviewedOpen && (
            <FadeIn>
              {!data ? (
                <Skeleton.Box height={64} />
              ) : reviewedCount > 0 ? (
                <ReviewItemsSection items={data.review_items ?? []} isReviewed showHeader={false} />
              ) : (
                <Box marginTop="12">
                  <Text size="small" color="subtle">
                    Nothing reviewed yet
                  </Text>
                </Box>
              )}
            </FadeIn>
          )}
        </Box>
      )}
    </Box>
  );
};
